import type { ExportedDatePickerToolbarProps } from '@mui/x-date-pickers/DatePicker/DatePickerToolbar';

/**
 * Session replay privacy configuration (SEE-1834).
 *
 * Sentry's defaults mask every text node and input and block every image and icon, which made
 * replays unreadable. This inverts them: `body` is in `unmask`, so everything is visible unless a
 * mask selector sits **closer** to the text than the nearest unmask selector (the SDK compares the
 * two distances; a tie masks). PII-dense screens put a mask marker on their root, and page chrome
 * (labels, helper/error text, buttons, headings, alerts) comes back via the unmask list.
 *
 * Rules worth knowing before adding a marker (full list in
 * docs/specs/SEE-1834-unmask-sentry-replay/plan.md, "How replay privacy actually works"):
 *
 * - The marker goes on the element that renders the PII text, never only on a wrapper: a heading
 *   (`Typography` `h1`-`h6`, `subtitle1/2` render `<h6>`) or button between the wrapper and the
 *   text is closer and unmasks it.
 * - Never wrap a text input in an unmasked element (`<label>`, `<Button component="label">`): the
 *   label is closer than any container marker and records the value in clear.
 * - Portals (Dialog, Drawer, Menu, Popover, Tooltip, Select/Autocomplete listboxes, DatePicker,
 *   snackbars) mount under `document.body`, out of reach of a container marker. Mark the portal.
 * - One invalid selector makes its whole list match nothing. The lists are parse-tested.
 */

/** Selectors Sentry always appends to `mask` (`getPrivacyOptions`). */
const SDK_DEFAULT_MASK_SELECTORS = ['.sentry-mask', '[data-sentry-mask]'];
/** Selectors Sentry always appends to `block`. */
const SDK_DEFAULT_BLOCK_SELECTORS = ['.sentry-block', '[data-sentry-block]', 'base[href="/"]'];
/** Inputs whose value rrweb masks unconditionally, whatever the selectors say. */
const SDK_ALWAYS_MASKED_AUTOCOMPLETE = [
  'current-password',
  'new-password',
  'cc-number',
  'cc-exp',
  'cc-exp-month',
  'cc-exp-year',
  'cc-csc',
];

export const REPLAY_MASK_SELECTORS: readonly string[] = [
  // Passwords. With `body` unmasked a password input is recorded in clear unless a mask selector
  // is at least as close; a show/hide toggle flips `type` to text but rrweb tags the live element.
  'input[type="password"]',
  'input[data-rr-is-password]',
  // Generic PII inputs. `mui-tel-input` renders `type="tel"`. `*=` catches `new-email` and
  // `email webauthn`; `~=` would not.
  'input[type="email"]',
  'input[type="tel"]',
  '[autocomplete*="email"]',
  '[autocomplete="username"]',
  '[autocomplete*="name"]',
  '[autocomplete*="address"]',
  '[autocomplete="postal-code"]',
  '[autocomplete="bday"]',
  '[autocomplete="one-time-code"]',
  // Out-of-tree roots no container marker reaches: Sendbird UIKit portals and Sentry's own
  // crash-report dialog (`<ErrorBoundary showDialog>` → `showReportDialog`).
  '#sendbird-modal-root',
  '#sendbird-dropdown-portal',
  '#sendbird-emoji-list-portal',
  '.sentry-error-embed-wrapper',
];

/** Page chrome MUI classes don't reach (`AuthButton`, auth/register error spans). */
export const REPLAY_CHROME_CLASS = 'replay-chrome';

export const REPLAY_UNMASK_SELECTORS: readonly string[] = [
  'body',
  'label',
  '.MuiFormLabel-root',
  '.MuiFormHelperText-root',
  '.MuiAlert-message',
  '.MuiAlertTitle-root',
  '.MuiButton-root',
  '.MuiStepLabel-label',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  `.${REPLAY_CHROME_CLASS}`,
];

/**
 * The SDK's `MEDIA_SELECTORS` minus `svg`, so icons render. `blockAllMedia` is `false` because it
 * would append `svg` back; this list is what keeps images, video and embeds blocked. `image` stays:
 * it embeds rasters inside an SVG.
 */
const MEDIA_BLOCK_SELECTORS = [
  'img',
  'image',
  'video',
  'object',
  'picture',
  'embed',
  'map',
  'audio',
  'link[rel="icon"]',
  'link[rel="apple-touch-icon"]',
];

export const REPLAY_BLOCK_SELECTORS: readonly string[] = [
  // Highnote card viewer, secure inputs and PIN containers. Cross-origin iframes are never recorded;
  // blocking the containers is defence in depth. Mobile ids carry a `-${paymentCardId}` suffix.
  '#cardNumber',
  '#expirationDate',
  '#cvv',
  '#pin',
  '[id^="cardNumber-"]',
  '[id^="expirationDate-"]',
  '[id^="cvv-"]',
  ...MEDIA_BLOCK_SELECTORS,
];

export const REPLAY_MASK_ATTRIBUTES: readonly string[] = ['title', 'aria-label', 'alt'];

/** Spread into `replayIntegration(...)` in `src/sentry.js`. */
export const REPLAY_PRIVACY_OPTIONS = {
  maskAllText: true,
  maskAllInputs: false,
  blockAllMedia: false,
  maskAttributes: [...REPLAY_MASK_ATTRIBUTES],
  mask: [...REPLAY_MASK_SELECTORS],
  unmask: [...REPLAY_UNMASK_SELECTORS],
  block: [...REPLAY_BLOCK_SELECTORS],
};

/**
 * `data-*` attributes only. Typed with an index signature so a marker can be passed where MUI
 * expects a props object (`slotProps.popper`, `MenuProps`, `ListboxProps`, `PaperProps`): those
 * are all-optional types, which reject an object with no property in common with them.
 */
export type DataAttributes = { [key: `data-${string}`]: string | boolean | undefined };

/** Spread on the element rendering PII (or on a portal root/paper/popper/listbox). */
export const sentryMask: DataAttributes = { 'data-sentry-mask': true };
/** Spread on a PCI container so it is replaced by a placeholder. */
export const sentryBlock: DataAttributes = { 'data-sentry-block': true };
/** Spread on chrome MUI classes don't reach, when the element has no other `className`. */
export const replayChrome = { className: REPLAY_CHROME_CLASS } as const;

/** `sentryMask` when `condition` holds, else nothing — for sites that are PII only sometimes. */
export function sentryMaskIf(condition: boolean): DataAttributes {
  return condition ? sentryMask : {};
}

/**
 * Merge into a DOB `DatePicker`'s `slotProps`. Its calendar is a portal (`popper` on desktop, a
 * `dialog` on touch devices, where `DatePicker` becomes `MobileDatePicker`). The mobile dialog's
 * toolbar shows the date as the text of an `h4`, and a heading is unmasked chrome, so a marker on
 * the toolbar root would lose. The toolbar forwards `classes.title` onto that `h4`, which then
 * matches mask and unmask at the same distance, and a tie masks.
 */
export const replayMaskedPickerSlotProps = {
  popper: sentryMask,
  dialog: sentryMask,
  // `classes` isn't in the exported toolbar slot type, but `DatePickerToolbar` reads it.
  toolbar: { classes: { title: 'sentry-mask' } } as ExportedDatePickerToolbarProps,
};

// ---------------------------------------------------------------------------------------------
// Test helpers. They mirror the SDK's decision (`needMaskingText`, `maskAttribute`,
// `_isBlockedElement` in @sentry-internal/replay 8.32.0) so DOM tests can assert what a replay
// would record. A bare `closest()` is not enough: the nearer of mask/unmask wins.
// ---------------------------------------------------------------------------------------------

const MASK_SELECTOR = [...REPLAY_MASK_SELECTORS, ...SDK_DEFAULT_MASK_SELECTORS].join(',');
const UNMASK_SELECTOR = REPLAY_UNMASK_SELECTORS.join(',');
const BLOCK_SELECTOR = [...REPLAY_BLOCK_SELECTORS, ...SDK_DEFAULT_BLOCK_SELECTORS].join(',');

/** The joined lists exactly as the SDK builds them (ours, then its defaults). */
export const REPLAY_SELECTOR_LISTS = {
  mask: [...REPLAY_MASK_SELECTORS, ...SDK_DEFAULT_MASK_SELECTORS],
  unmask: [...REPLAY_UNMASK_SELECTORS],
  block: [...REPLAY_BLOCK_SELECTORS, ...SDK_DEFAULT_BLOCK_SELECTORS],
};

/** True if the joined list parses as one selector — an invalid entry breaks every match. */
export function selectorListParses(selectors: readonly string[], root: ParentNode = document): boolean {
  try {
    root.querySelector(selectors.join(','));
    return true;
  } catch {
    return false;
  }
}

function distanceToMatch(el: Element | null, selector: string, limit = Infinity): number {
  let distance = 0;
  for (let node = el; node; node = node.parentElement, distance += 1) {
    if (distance > limit) return -1;
    if (node.matches(selector)) return distance;
  }
  return -1;
}

const INPUT_TAGS = ['INPUT', 'TEXTAREA', 'SELECT', 'OPTION'];

function inputType(el: Element): string | null {
  if (el.hasAttribute('data-rr-is-password')) return 'password';
  const type = (el as HTMLInputElement).type;
  return type ? type.toLowerCase() : null;
}

/**
 * Would the replay mask this element's text (or, for an input, its value)? Mirrors
 * `needMaskingText`: for text the "mask all" flag is `maskAllText`; for inputs it is
 * `shouldMaskInput`, which with `maskAllInputs: false` is true only for passwords.
 */
export function isReplayMasked(node: Node): boolean {
  const el = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
  if (!el) return false;
  if (
    el.tagName === 'INPUT' &&
    SDK_ALWAYS_MASKED_AUTOCOMPLETE.includes(el.getAttribute('autocomplete') ?? '')
  ) {
    return true;
  }
  const maskAll = INPUT_TAGS.includes(el.tagName)
    ? inputType(el) === 'password'
    : REPLAY_PRIVACY_OPTIONS.maskAllText;
  if (maskAll) {
    const unmaskDistance = distanceToMatch(el, UNMASK_SELECTOR);
    if (unmaskDistance < 0) return true;
    const maskDistance = distanceToMatch(el, MASK_SELECTOR, unmaskDistance);
    return maskDistance >= 0 && maskDistance <= unmaskDistance;
  }
  const maskDistance = distanceToMatch(el, MASK_SELECTOR);
  if (maskDistance < 0) return false;
  const unmaskDistance = distanceToMatch(el, UNMASK_SELECTOR, maskDistance);
  return unmaskDistance < 0 || maskDistance <= unmaskDistance;
}

/** Would the replay drop this element (and its subtree) for a placeholder? */
export function isReplayBlocked(el: Element): boolean {
  return el.closest(BLOCK_SELECTOR) !== null;
}

/**
 * Would the replay mask this attribute? Mirrors `maskAttribute`: every attribute in
 * `maskAttributes` is masked unless the element **itself** matches an unmask selector.
 */
export function isAttributeMasked(el: Element, name: string): boolean {
  if (!REPLAY_PRIVACY_OPTIONS.maskAllText) return false;
  if (el.matches(UNMASK_SELECTOR)) return false;
  return REPLAY_MASK_ATTRIBUTES.includes(name);
}
